import { identity } from "@core/foundation/function";
import {
  createModel,
  createModelSchemas,
  noModelUpdate,
} from "@core/foundation/model";
import { singleline } from "@core/foundation/string";
import { z } from "@core/zod";

export const GithubPullRequest = createModel({
  create: (item, schema) => schema.decode(item),
  schemas: createModelSchemas({
    base: z.object(
      {
        author: z.string().nullable(),
        checksState: z
          .enum(["error", "expected", "failure", "pending", "success"])
          .nullable(),
        createdAt: z.string(),
        headRef: z.string(),
        headSha: z.string(),
        htmlUrl: z.url(),
        isInMergeQueue: z.boolean(),
        mergeCommitSha: z.string().nullable(),
        mergeable: z.enum(["conflicting", "mergeable", "unknown"]),
        merged: z.boolean(),
        number: z.int().positive(),
        reviewDecision: z
          .enum(["approved", "changes_requested", "review_required"])
          .nullable(),
        state: z.enum(["closed", "open"]),
        title: z.string(),
      },
      {
        author: "The author's login, or null when GitHub names none.",
        checksState: singleline`
          The rolled-up state of the head commit's checks, or null when none
          ran.
        `,
        createdAt: "When GitHub created the pull request.",
        headRef: "The branch the pull request merges from.",
        headSha: "The SHA of the pull request's head commit.",
        htmlUrl: "The pull request's page on GitHub.",
        isInMergeQueue: "Whether the pull request sits in the merge queue.",
        mergeCommitSha: singleline`
          The SHA of the pull request's merge commit, or null when it has none.
        `,
        mergeable: singleline`
          Whether the pull request merges cleanly, or unknown while GitHub
          computes it.
        `,
        merged: "Whether the pull request merged.",
        number: "The pull request's number.",
        reviewDecision: singleline`
          The pull request's review decision, or null when GitHub reports none.
        `,
        state: "Whether the pull request is open or closed.",
        title: "The pull request's title.",
      },
    ),
    derive: { create: identity, recovery: identity, update: noModelUpdate },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface GithubPullRequest
  extends z.infer<typeof GithubPullRequest.schema> {}
